import orderBy from 'lodash/orderBy';
import React, { useState } from 'react';
import { Box, IconButton, Theme, Tooltip, Typography } from '@mui/material';
import { Feature, getLocalizedProperty } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MembershipAttributes, ProfileAttributes } from '@loyalty/shared-common/profile-api-client';
import { colorPalette } from '@qualifio/ui-commons';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { makeStyles } from '@mui/styles';
import { renderLevelExpiration } from '@loyalty/editor-components';
import { useTranslation } from 'react-i18next';
import { AdjustLevelDialog } from './AdjustLevelDialog';
import { LevelImage } from '../../../shared/level/LevelImage';
import { useFeatures } from '../../../features/FeaturesContext';
import { useLevels } from '../../../levels/LevelsContext';
import { useLevelsConfig } from '../../../levels/LevelsConfigContext';

interface Props {
  profile: ProfileAttributes;
  membership: MembershipAttributes;
  onLevelAdjusted?: () => Promise<void> | void;
}

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  levelText: {
    padding: spacing(2),
  },
  placeholder: {
    color: colorPalette.grey.mainHover,
  },
  iconButton: {
    color: colorPalette.grey.main,
    '&:hover': {
      backgroundColor: 'inherit',
      color: colorPalette.turquoise.main,
    },
  },
}));

export function ProfileLevel({ profile, membership, onLevelAdjusted }: Props): React.ReactElement {
  const { levels } = useLevels();
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const classes = useStyles();
  const { features } = useFeatures();
  const { enabled: adjustLevelEnabled } = features[Feature.ADJUST_LEVEL];
  const [adjustedLevel, setAdjustedLevel] = useState<boolean>();
  const { levelsConfig } = useLevelsConfig();
  const sortedLevels = orderBy(levels, ['minCurrencyAmount']);
  const currentLevel = membership.level
    ? levels.find(({ id }) => id === membership.level?.current)
    : undefined;
  const nextLevel = sortedLevels.find(
    l => !currentLevel || l.minCurrencyAmount > (currentLevel.minCurrencyAmount ?? 0),
  );

  return (
    <Box display="flex" alignItems="center">
      {currentLevel ? (
        <>
          <Tooltip
            title={renderLevelExpiration(
              levelsConfig,
              { t, locale: i18n.language },
              { currentLevel, nextLevel, membership },
              'backoffice',
            )}
          >
            <div>
              <LevelImage level={currentLevel} />
            </div>
          </Tooltip>

          <Typography variant="body1" component="div" className={classes.levelText}>
            {getLocalizedProperty(currentLevel, 'name', i18n.language)}
          </Typography>
        </>
      ) : (
        <Box className={classes.placeholder}>{t('profiles.details.level.placeholder')}</Box>
      )}
      {!!levelsConfig && (
        <Tooltip title={adjustLevelEnabled ? '' : t('shared.action.permission.required')}>
          <div>
            <IconButton
              disabled={!adjustLevelEnabled}
              className={classes.iconButton}
              onClick={() => setAdjustedLevel(true)}
              id={'adjust-balance-button-id'}
            >
              <FontAwesomeIcon size="xs" icon={faPencil} />
            </IconButton>
          </div>
        </Tooltip>
      )}
      {adjustedLevel && (
        <AdjustLevelDialog
          profile={profile}
          membership={membership}
          onClose={() => setAdjustedLevel(false)}
          onApply={onLevelAdjusted}
        />
      )}
    </Box>
  );
}
